import { useEffect, useRef, type JSX, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { BarreEvenement } from './BarreEvenement.js';
import { ElementEvenement } from './ElementEvenement.js';
import {
  barresDeSemaine,
  estJourneeEntiere,
  formaterJour,
  heureDeMinutes,
  joursDeLaSemaine,
  MINUTES_PAR_JOUR,
  placerSegments,
  segmentsALHeureParJour,
  type SegmentJour,
} from './periodes.js';
import type { PropsVue } from './types.js';
import styles from './Calendrier.module.css';

/** Heure de la journée en haut de la grille au premier affichage. */
const HEURE_DE_DEPART = 7;
const HEURES = Array.from({ length: 24 }, (_, heure) => heure);

/** La semaine : jours en colonnes, journées entières en haut, rendez-vous à l'heure. */
export function VueSemaine({
  ancre,
  evenements,
  aujourdhui,
  langue,
  onCreer,
}: PropsVue): JSX.Element {
  const { t } = useTranslation('regie');
  const jours = joursDeLaSemaine(ancre);
  const barres = barresDeSemaine(evenements, jours[0]!);
  const voies = Math.max(
    1,
    barres.reduce((max, barre) => Math.max(max, barre.voie + 1), 0),
  );
  const parJour = segmentsALHeureParJour(evenements, jours[0]!, jours[6]!);
  const defilement = useRef<HTMLDivElement>(null);

  // La nuit ne sert presque jamais : la grille s'ouvre sur le matin, ou plus
  // tôt si un rendez-vous de la semaine commence avant.
  const premiereMinute = Math.min(
    HEURE_DE_DEPART * 60,
    ...[...parJour.values()].flat().map((segment) => segment.debutMinute),
  );
  useEffect(() => {
    const element = defilement.current;
    if (element) element.scrollTop = Math.max(0, (premiereMinute / 60) * heurePixels(element) - 8);
  }, [ancre, premiereMinute]);

  function creerSurColonne(jour: string, evenement: MouseEvent<HTMLDivElement>): void {
    if (!onCreer || evenement.target !== evenement.currentTarget) return;
    const boite = evenement.currentTarget.getBoundingClientRect();
    const heure = Math.floor(((evenement.clientY - boite.top) / boite.height) * 24);
    onCreer(
      jour,
      Number.isFinite(heure) ? heureDeMinutes(Math.min(23, Math.max(0, heure)) * 60) : undefined,
    );
  }

  return (
    <div
      className={styles.semaine}
      data-ui="calendrier-semaine"
      role="group"
      aria-label={t('calendrier.grille.semaine')}
    >
      <div className={styles.semaineEntete}>
        <span />
        {jours.map((jour) => (
          <div
            key={jour}
            className={styles.enteteJour}
            data-aujourdhui={jour === aujourdhui ? 'oui' : undefined}
          >
            <span>{formaterJour(jour, langue, { weekday: 'short' })}</span>
            <span className={styles.numeroJour}>{Number(jour.slice(8))}</span>
          </div>
        ))}
      </div>

      <div
        className={styles.semaineJournee}
        style={{ gridTemplateRows: `repeat(${voies}, auto)` }}
        data-ui="calendrier-journee"
      >
        <span className={styles.gouttiereJournee} style={{ gridColumn: 1, gridRow: '1 / -1' }}>
          {t('calendrier.touteLaJournee')}
        </span>
        {jours.map((jour, colonne) => (
          <div
            key={jour}
            className={styles.cellule}
            style={{ gridColumn: colonne + 2, gridRow: '1 / -1' }}
            data-aujourdhui={jour === aujourdhui ? 'oui' : undefined}
            data-creable={onCreer ? 'oui' : undefined}
            onClick={onCreer ? () => onCreer(jour) : undefined}
          />
        ))}
        {barres.map((barre) => (
          <BarreEvenement
            key={barre.evenement.id}
            barre={barre}
            premiereLigne={1}
            decalageColonne={1}
          />
        ))}
      </div>

      <div className={styles.defilement} ref={defilement}>
        <div className={styles.grilleHeures}>
          <div className={styles.gouttiere} aria-hidden>
            {HEURES.map((heure) => (
              <span key={heure} style={{ top: `calc(var(--heure-px) * ${heure})` }}>
                {heure === 0 ? '' : heureDeMinutes(heure * 60)}
              </span>
            ))}
          </div>
          {jours.map((jour) => (
            <div
              key={jour}
              className={styles.colonneJour}
              data-aujourdhui={jour === aujourdhui ? 'oui' : undefined}
              data-creable={onCreer ? 'oui' : undefined}
              data-jour={jour}
              onClick={(evenement) => creerSurColonne(jour, evenement)}
            >
              {placerSegments(parJour.get(jour) ?? []).map((segment) => (
                <div
                  key={`${segment.evenement.id}-${jour}`}
                  className={styles.bloc}
                  style={{
                    top: `calc(var(--heure-px) * ${segment.debutMinute / 60})`,
                    height: `calc(var(--heure-px) * ${
                      Math.max(segment.finMinute - segment.debutMinute, 30) / 60
                    })`,
                    left: `${(segment.colonne / segment.colonnes) * 100}%`,
                    width: `${100 / segment.colonnes}%`,
                  }}
                  data-teinte={segment.evenement.teinte}
                  data-evidence={segment.evenement.evidence ? 'oui' : undefined}
                  data-debut-coupe={segment.debutCoupe ? 'oui' : undefined}
                  data-fin-coupee={segment.finCoupee ? 'oui' : undefined}
                >
                  <ElementEvenement
                    evenement={segment.evenement}
                    className={styles.lienBloc}
                    titre={segment.evenement.titre}
                  >
                    <span className={styles.heureBloc}>{plageHoraire(segment)}</span>
                    <span className={styles.titreBloc}>{segment.evenement.titre}</span>
                    {segment.evenement.detail ? (
                      <span className={styles.detailBloc}>{segment.evenement.detail}</span>
                    ) : null}
                  </ElementEvenement>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** « 10:00–11:00 », ou « 10:00 » quand la fin est inconnue. */
export function plageHoraire(segment: SegmentJour): string {
  const debut = heureDeMinutes(segment.debutMinute);
  if (segment.evenement.minuteFin === null && !segment.finCoupee) return debut;
  return `${debut}–${segment.finMinute >= MINUTES_PAR_JOUR ? '24:00' : heureDeMinutes(segment.finMinute)}`;
}

function heurePixels(element: HTMLElement): number {
  const valeur = Number.parseFloat(getComputedStyle(element).getPropertyValue('--heure-px'));
  return Number.isFinite(valeur) ? valeur : 48;
}

/**
 * La semaine sur téléphone : une liste de jours, de lundi à dimanche, chacun
 * avec ses journées entières puis ses rendez-vous à l'heure.
 */
export function VueSemaineCondensee({
  ancre,
  evenements,
  aujourdhui,
  langue,
  onCreer,
}: PropsVue): JSX.Element {
  const { t } = useTranslation('regie');
  const jours = joursDeLaSemaine(ancre);
  const parJour = segmentsALHeureParJour(evenements, jours[0]!, jours[6]!);
  return (
    <div
      className={styles.condensee}
      data-ui="calendrier-semaine-condensee"
      role="group"
      aria-label={t('calendrier.grille.semaine')}
    >
      {jours.map((jour) => {
        const entiers = evenements
          .filter((e) => estJourneeEntiere(e) && e.jourDebut <= jour && e.jourFin >= jour)
          .sort((a, b) => a.titre.localeCompare(b.titre));
        const alHeure = parJour.get(jour) ?? [];
        const libelle = formaterJour(jour, langue, {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
        });
        return (
          <section
            key={jour}
            className={styles.jourCondense}
            data-aujourdhui={jour === aujourdhui ? 'oui' : undefined}
            aria-label={libelle}
          >
            <header className={styles.enteteJourCondense}>
              <h3>{libelle}</h3>
              {onCreer ? (
                <button
                  type="button"
                  className={styles.ajouter}
                  aria-label={t('calendrier.creerLe', { date: libelle })}
                  onClick={() => onCreer(jour)}
                >
                  +
                </button>
              ) : null}
            </header>
            {entiers.length || alHeure.length ? (
              <ul className={styles.listeCondensee}>
                {entiers.map((evenement) => (
                  <li
                    key={evenement.id}
                    data-teinte={evenement.teinte}
                    data-evidence={evenement.evidence ? 'oui' : undefined}
                  >
                    <span className={styles.heureCondensee}>{t('calendrier.touteLaJournee')}</span>
                    <ElementEvenement evenement={evenement} className={styles.lienCondense}>
                      {evenement.titre}
                    </ElementEvenement>
                  </li>
                ))}
                {alHeure.map((segment) => (
                  <li
                    key={`${segment.evenement.id}-${jour}`}
                    data-teinte={segment.evenement.teinte}
                    data-evidence={segment.evenement.evidence ? 'oui' : undefined}
                  >
                    <span className={styles.heureCondensee}>{plageHoraire(segment)}</span>
                    <ElementEvenement evenement={segment.evenement} className={styles.lienCondense}>
                      {segment.evenement.titre}
                    </ElementEvenement>
                    {segment.evenement.detail ? (
                      <span className={styles.detailCondense}>{segment.evenement.detail}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p className={styles.videJour}>{t('calendrier.videJour')}</p>
            )}
          </section>
        );
      })}
    </div>
  );
}
